import React from 'react';
import { AddressFind, LandingPad, Order, OrderHubCard, PublicOrder, OrderSummary, Merchant } from '@m4a3/api-client';
import { ReactComponent as RouteLine } from '../assets/svgs/RouteLine.svg';
import { ReactComponent as DropPoint } from '../assets/svgs/DropPoint.svg';
import { isEqual } from 'lodash';
import { espooCellId, inactiveStatuses } from '../constants';
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner';
import { getDropPointRemarkReasons } from './addressFindUtils';
import { store } from '../store/index';

export function shouldLoadOrderNow(order: Order): boolean {
  return (
    ['READY', 'RECEIVED_FROM_STORE'].includes(order?.status) &&
    order?.tags?.includes('LOAD_NOW')
  );
}

export function isLoadingOrder(order: Order): boolean {
  return (
    ['READY', 'RECEIVED_FROM_STORE'].includes(order?.status) &&
    order?.tags?.includes('LOADING_DRONE')
  );
}

export function isWaitingForMCToApproveLoad(order: Order): boolean {
  return (
    ['READY', 'RECEIVED_FROM_STORE'].includes(order?.status) &&
    !order?.tags?.includes('LOAD_NOW') &&
    !isLoadingOrder(order)
  );
}

export function getAssignedTeamForOrder(order: Order): string {
  return order.tags?.find(tag => tag.includes('TEAM'));
}

export function getAssignedLaneForOrder(order: Order): string {
  return order.tags?.find(tag => tag.includes('LANE'));
}

export function getAssignedAircraftForOrder(order: Order): string {
  return order.aircraft;
}

export function hasRouteForOrder(order: Order): boolean {
  return (
    (!!order.mission || !!order.missionId) &&
    ((order as any).missionStatus === 'HOLD' ||
      order.mission?.status === 'HOLD')
  );
}

export function orderHasPadsAssigned(order: Order): boolean {
  return !!order.originLandingPadId && !!order.destinationLandingPadId;
}

export const isDueIn30Mins = (order: Order): boolean => {
  return (
    (new Date(order.expectedDeliveryDate).getTime() > new Date().getTime() &&
      Math.floor(
        (new Date(order.expectedDeliveryDate).getTime() -
          new Date().getTime()) /
          60000
      ) < 30) ||
    new Date(order.expectedDeliveryDate).getTime() < new Date().getTime()
  );
};

export const getElementForDropPointStatus = (order: Order): JSX.Element => {
  switch (order.dropPointStatus) {
    case 'READY':
      return <span>✅</span>;
    case 'PENDING_REVIEW':
      return <span>💬</span>;
    case 'PENDING':
    default:
      return <LoadingSpinner size={20} />;
  }
};

export const getDeliveryLocationContent = (addressFind: AddressFind): string => {

 const isCellEspoo = addressFind?.cellId === espooCellId;

  if (addressFind?.suggestedDropLocations?.length === 0 && !addressFind?.preferredDropLocationId && !addressFind?.isPendingReview) {
    return isCellEspoo ? 'Waiting for MC to set drop location' : 'Waiting for MC to provide suggested delivery points';
  } else if (addressFind?.preferredDropLocationId) {
    return isCellEspoo ? '✅ Delivery spot set' : '✅ Delivery spot selected and cleared by customer';
  } else if (addressFind?.isPendingReview && getDropPointRemarkReasons(addressFind)?.length > 0) {
    const remarks = getDropPointRemarkReasons(addressFind);
    return `💬 Change delivery spot due to "${remarks.join(',') || 'obstruction'}"`;
  }

  return isCellEspoo ? 'Set a new drop location now' : 'Waiting for customer to select delivery spot';
};

export const isDeliverySpotSet = (order: Order): boolean => {

  if (!order) return false;
  
  return (
    !(order?.needsDropLocationReview &&
    order?.dropPointStatus === 'PENDING')
  );
};

export const shouldShowDropPointConfirmationForOrder = (order: Order): boolean => {
  return (
    !hasRouteForOrder(order) &&
    ![...inactiveStatuses, 'DISPATCHED', 'DELIVERED'].includes(order.status) &&
    order.needsDropLocationReview &&
    order.dropPointStatus === 'PENDING'
  );
};

export const getOrderActionCardData = (
  order: Order
): { buttonText: string; iconComponent: JSX.Element } => {
  if (
    !hasRouteForOrder(order) &&
    ['RECEIVED_FROM_STORE'].includes(order.status) &&
    orderHasPadsAssigned(order)
  ) {
    return {
      buttonText: 'Confirm Route',
      iconComponent: <RouteLine width={21} height={21} />
    };
  }

  if (shouldShowDropPointConfirmationForOrder(order)) {
    return {
      buttonText: 'Suggest delivery spot',
      iconComponent: <DropPoint width={21} height={21} />
    };
  }

  return {
    buttonText: '',
    iconComponent: <></>
  };
};

export const areOrdersEqual = (
  prevOrder: Readonly<{ order: Order; addressFind?: AddressFind; landingZones?: Array<{ id: string; name: string }> }>,
  nextOrder: Readonly<{ order: Order; addressFind?: AddressFind; landingZones?: Array<{ id: string; name: string }> }>
): boolean => {

  const state = store.getState();

  const prevOverweightPairings = state.orders?.orderIdsWithOverweightColorPairings?.[prevOrder.order.orderShortId];
  const nextOverweightPairings = state.orders?.orderIdsWithOverweightColorPairings?.[nextOrder.order.orderShortId];

  // Compare overweightColorPairings - need to re-render if there is new colouring to show
  if (!isEqual(prevOverweightPairings, nextOverweightPairings)) return false;

  /*
  return true if passing nextProps to render would return
  the same result as passing prevProps to render,
  otherwise return false
  */
  if (prevOrder.addressFind?.preferredDropLocationId !== nextOrder.addressFind?.preferredDropLocationId) return false;
  if (prevOrder.addressFind?.isPendingReview !== nextOrder.addressFind?.isPendingReview) return false;
  if (prevOrder.addressFind?.suggestedDropLocations?.length !== nextOrder.addressFind?.suggestedDropLocations?.length) return false;
  if (prevOrder.order.status !== nextOrder.order.status) return false;
  if (prevOrder.order.aircraft !== nextOrder.order.aircraft) return false;
  // Check for changes in the departure and arrival pad values
  if (prevOrder.order.originLandingPadId !== nextOrder.order.originLandingPadId) return false;
  if (prevOrder.order.destinationLandingPadId !== nextOrder.order.destinationLandingPadId) return false;
  if (prevOrder.order.acceptedForDate !== nextOrder.order.acceptedForDate)
    return false;
  if (
    prevOrder.order.expectedDeliveryDate !==
    nextOrder.order.expectedDeliveryDate
  )
    return false;
  if (prevOrder.order.eircode !== nextOrder.order.eircode) return false;
  if (prevOrder.order.sort !== nextOrder.order.sort) return false;
  if (prevOrder.order.swimlane !== nextOrder.order.swimlane) return false;
  if (prevOrder.order.teamStatus !== nextOrder.order.teamStatus) return false;
  if (prevOrder.order.collectorStatus !== nextOrder.order.collectorStatus) return false;
  if (prevOrder.order.mcHasConfirmedDropPoint !== nextOrder.order.mcHasConfirmedDropPoint) return false;
  if (prevOrder.order.dropPointStatus !== nextOrder.order.dropPointStatus) return false;
  if (prevOrder.order.needsDropLocationReview !== nextOrder.order.needsDropLocationReview) return false;
  if (prevOrder.order.takeOffTimeMs !== nextOrder.order.takeOffTimeMs) return false;
  if ((prevOrder.order as any).landingZoneId !== (nextOrder.order as any).landingZoneId) return false;

  if (prevOrder.order.actualWeight !== nextOrder.order.actualWeight)
    return false;
  if (
    (prevOrder.order as any).missionStatus !==
    (nextOrder.order as any).missionStatus
  )
    return false;
  if (!isEqual(prevOrder.order.tags, nextOrder.order.tags)) return false;

  // Re-render if landing zone options provided to OrderCard have changed (order-insensitive, stable subset)
  const prevLandingZones = (prevOrder as any)?.landingZones as Array<{ id: string; name: string }> | undefined;
  const nextLandingZones = (nextOrder as any)?.landingZones as Array<{ id: string; name: string }> | undefined;
  const normalizeZones = (zones?: Array<{ id: string; name: string }>) =>
    (zones || [])
      .map(z => ({ id: z?.id, name: z?.name }))
      .sort((a, b) => String(a.id).localeCompare(String(b.id)));
  if (!isEqual(normalizeZones(prevLandingZones), normalizeZones(nextLandingZones))) return false;

  return true;
};

export const isOrderLate = (order: Order) => {
  const THREE_MINUTES_FOR_DELIVERY = 180000;
  const dueTime =
    new Date(order.expectedDeliveryDate).getTime() - THREE_MINUTES_FOR_DELIVERY;
  const currentTime = new Date().getTime();

  return dueTime < currentTime;
};

export const getShortIdForOrder = (order: Order | PublicOrder | OrderHubCard | OrderSummary): string => {
  if (order && 'orderShortId' in order && order.orderShortId) {
    return order.orderShortId;
  }
  return order?.id?.slice(0, 4) ?? 'Unknown';
};

export const renderOrderKm = (distance: number): string => {
  const warningFarDistance = 5.5;
  const cautionDistance = 5;

  if (distance === null || distance === undefined || isNaN(distance)) {
    return '(?km)';
  }
  if (distance >= warningFarDistance) {
    return `(❌ ${distance}km)`;
  } else if (distance >= cautionDistance) {
    return `(⚠️ ${distance}km)`;
  } else {
    return `(${distance}km)`;
  }
};

/**
 * Checks if the distance to an order is too far (>= 5.5km)
 * @param distance - Distance in kilometers
 * @returns true if distance is >= 5.5km, false otherwise
 */
export const isDistanceFarToFly = (distance: number): boolean => {
  
  const warningFarDistance = 5;
  
  if (distance === null || distance === undefined || isNaN(distance)) {
    return false;
  }
  
  return distance >= warningFarDistance;
};

export const weightStringToNumberInKgs = (weightAsString: string): number => {
  const normalisedVal = weightAsString?.replace(/[^0-9.]/g, ''); // remove anything but numbers and decimal points
  return parseFloat(normalisedVal);
};

const newOrderPreparedSound = new Audio(require('../assets/alert_simple.wav'));
newOrderPreparedSound.volume = 1;

export const playNewOrderSound = async () => {
  try {
    await newOrderPreparedSound.play()
  } catch (error) {
    // noop - this throws an error if it's played before the user has interacted with the page.
  }
};

const overweightPairingColors = [
  "#FF6B6B", /* Vibrant Coral */
  "#FFD700", /* Bright Sunflower */
  "#00E676", /* Neon Green */
  "#009DFF", /* Electric Blue */
  "#9C27B0", /* Deep Purple */
  "#FF6F61", /* Hot Peach */
  "#00CED1", /* Aqua Cyan */
  "#BFFF00", /* Neon Lime */
  "#4A90E2", /* Sky Blue */
  "#50E3C2", /* Mint Green */
  "#F39C12", /* Amber Orange */
  "#8E44AD", /* Rich Purple */
  "#FF5252", /* Bright Red */
  "#2ECC71", /* Emerald Green */
  "#FFA726", /* Warm Orange */
  "#7986CB", /* Cornflower Blue */
  "#F06292", /* Pink */
  "#26A69A", /* Teal */
  "#FF4081", /* Deep Pink */
  "#7CB342", /* Lime Green */
  "#5C6BC0", /* Indigo */
  "#EC407A", /* Rose */
  "#AB47BC", /* Orchid Purple */
  "#66BB6A", /* Leaf Green */
  "#FFC107", /* Amber Yellow */
  "#42A5F5", /* Clear Blue */
  "#EF5350", /* Tomato Red */
  "#FF7043", /* Deep Orange */
  "#8D6E63", /* Warm Brown */
  "#78909C", /* Blue Grey */
  "#29B6F6", /* Light Blue */
  "#26C6DA"  /* Light Cyan */
];

const checkForExraDeliveryStores = (ordersWithSameEircode: Order[]) => {
  return ordersWithSameEircode.some(o => o.outletName.startsWith('Extra Delivery'));
}

export const isExtraDeliveryOrder = (order: Order): boolean => {
  return !!order?.outletName?.startsWith('Extra Delivery');
}

export const isInHouseMerchant = (posId: number, merchants?: Merchant[]): boolean => { 

  // Default in-house store posIds (fallback if merchants not provided)
  const defaultInHouseStorePosIds = [
    6,       // The Ice Cream Shop
    10,      // Coca-Cola
    16,      // The Ice Cream Shop
    32,      // Manna Mini Mart
    33,      // Heavenly Café
    960646,  // Kerry Test Store
    1050527, // Offbeat Donuts
    1780322  // Offbeat Donuts
  ];
  
  // Use merchants with isInHouse=true if provided, otherwise use defaults
  const inHouseStorePosIds = merchants?.length 
    ? merchants.filter(m => m.isInHouse).map(m => m.id) 
    : defaultInHouseStorePosIds;
  
  return inHouseStorePosIds.includes(posId);
}

// Sequential color assignment that ensures deterministic behavior across browser sessions
const getColorForEircodeGroup = (ordersWithSameEircode: Order[], allGroupKeys: string[], groupKey: string): string => {
  if (ordersWithSameEircode.length === 0) return overweightPairingColors[0];

  // Sort all group keys deterministically to ensure consistent ordering across sessions
  const sortedGroupKeys = [...allGroupKeys].sort((a, b) => {
    // Use a simple string comparison for deterministic sorting
    // This ensures the same group keys always appear in the same order
    if (a < b) return -1;
    if (a > b) return 1;
    return 0;
  });

  // Find the position of this group key in the sorted list
  const eircodeIndex = sortedGroupKeys.indexOf(groupKey);

  // Assign colors sequentially based on position
  // This ensures we use each color once before reusing any
  const colorIndex = eircodeIndex % overweightPairingColors.length;

  return overweightPairingColors[colorIndex];
};

export const findOverweightOrderPairings = ({
  ordersToFilter,  
  existingColorPairings
}: {
  ordersToFilter: Order[],
  existingColorPairings: { [key: string]: { color: string, eircode: string, linkedOverweightOrderIds: string[] } }
}) => {

  /**
   * Logic: Group orders by eircode first, then determine which groups to include.
   * If ANY order in an eircode group is in progress (not in NOT_IN_PROGRESS_STATUSES),
   * then ALL orders in that group should retain their links.
   * 
   * Examples:
   * - A RESET, B COMPLETE, C DELIVERED → No linking (all completed)
   * - A READY, B COMPLETE, C COMPLETE → All keep links (A is in progress)
   * - A RESET, B RESET, C DISPATCHED → All keep links (C is in progress)
   */

  // Group by eircode and filter to include only groups with at least one in-progress order
  const validEircodeGroups = new Map<string, Order[]>();
  const includedOrderIds = new Set<string>();

  // Group orders by eircode
  const eircodeGroups = new Map<string, Order[]>();
  ordersToFilter.forEach(order => {
    if (!eircodeGroups.has(order.eircode)) {
      eircodeGroups.set(order.eircode, [order]);
    } else {
      eircodeGroups.get(order.eircode)!.push(order);
    }
  });

  // Split each eircode group by time window (2.5 hours)
  const TIME_WINDOW_MS = 2.5 * 60 * 60 * 1000; // 2.5 hours in milliseconds
  const eircodeTimeGroups = new Map<string, Order[]>();
  let groupIndex = 0;

  eircodeGroups.forEach((orders, eircode) => {
    // Sort orders by expectedDeliveryDate
    const sortedOrders = [...orders].sort((a, b) => 
      new Date(a.expectedDeliveryDate).getTime() - new Date(b.expectedDeliveryDate).getTime()
    );

    // Split into time-based sub-groups
    let currentGroup: Order[] = [];
    
    sortedOrders.forEach((order, index) => {
      if (currentGroup.length === 0) {
        currentGroup.push(order);
      } else {
        // Check if this order is within 2.5 hours of the FIRST order in the current group
        const firstOrderTime = new Date(currentGroup[0].expectedDeliveryDate).getTime();
        const currentOrderTime = new Date(order.expectedDeliveryDate).getTime();
        
        if (currentOrderTime - firstOrderTime <= TIME_WINDOW_MS) {
          currentGroup.push(order);
        } else {
          // Save current group and start a new one
          eircodeTimeGroups.set(`${eircode}_${groupIndex++}`, currentGroup);
          currentGroup = [order];
        }
      }
      
      // Add the last group when we reach the end
      if (index === sortedOrders.length - 1 && currentGroup.length > 0) {
        eircodeTimeGroups.set(`${eircode}_${groupIndex++}`, currentGroup);
      }
    });
  });

  // Filter groups that have at least one in-progress order
  eircodeTimeGroups.forEach((ordersByGroup, groupKey) => {
    const hasInProgressOrder = ordersByGroup.some(order => !NOT_IN_PROGRESS_STATUSES.includes(order.status));
    
    if (hasInProgressOrder) {
      validEircodeGroups.set(groupKey, ordersByGroup);
      ordersByGroup.forEach(order => includedOrderIds.add(order.orderShortId));
    }
  });

  // Filter existing color pairings to only include valid orders
  const existingValidColourPairings = Object.entries(existingColorPairings)
                                .filter(([orderId]) => includedOrderIds.has(orderId))
                                .reduce((accumulator, [orderId, value]) => {
                                  accumulator[orderId] = value;
                                  return accumulator;
                                }, {} as typeof existingColorPairings);

    /**

    eircodeMap example 
    
    eircodeMap {
      "D15ABCD" => [
        OrderObj,
        OrderObj
      ],
      "D15EFGH" => [
        OrderObj,
      ],
      "D15IJKL" => [
        OrderObj,
        OrderObj,
        OrderObj
      ]
    }
  */

  // Clean up existing pairings: remove invalid linked orders and check Extra Delivery requirements
  const groupDeliveryCheck = new Map<string, boolean>();
  
  // Create a map of orderId to groupKey for quick lookup
  const orderIdToGroupKey = new Map<string, string>();
  validEircodeGroups.forEach((orders, groupKey) => {
    orders.forEach(order => {
      orderIdToGroupKey.set(order.orderShortId, groupKey);
    });
  });
  
  Object.keys(existingValidColourPairings).forEach(orderId => {
    const groupKey = orderIdToGroupKey.get(orderId);
    
    if (!groupKey) {
      // Order not in any valid group, remove it
      delete existingValidColourPairings[orderId];
      return;
    }
    
    const ordersInGroup = validEircodeGroups.get(groupKey) || [];
    const orderIdsInGroup = new Set(ordersInGroup.map(o => o.orderShortId));
    
    // Check Extra Delivery store requirement (cache results per group)
    if (!groupDeliveryCheck.has(groupKey)) {
      groupDeliveryCheck.set(groupKey, 
        ordersInGroup.length > 1 && checkForExraDeliveryStores(ordersInGroup)
      );
    }
    
    // Filter linked orders to only include valid ones in the same time-based group
    const validLinkedOrders = existingValidColourPairings[orderId].linkedOverweightOrderIds
      .filter(linkedId => orderIdsInGroup.has(linkedId));
    
    // Remove pairing if no valid links remain or group lacks Extra Delivery store
    if (validLinkedOrders.length === 0 || !groupDeliveryCheck.get(groupKey)) {
      delete existingValidColourPairings[orderId];
    } else {
      existingValidColourPairings[orderId].linkedOverweightOrderIds = validLinkedOrders;
    }
  });

  const orderIdsWithMarkedPairings: {
    [key: string]: { color: string; eircode: string; linkedOverweightOrderIds: string[] }
  } = { ...existingValidColourPairings }; //Currently this will only work within the same browser session - reloading the page will empty the store 

  /**
   * Assigns a color to overweight orders and links them together.
   *
   * ordersByEircode: Array of orders
   * eircode: String (actual eircode for storing in data)
   * groupKey: String (unique key for color assignment)
   */
  const assignOverweightOrderPairing = (ordersByEircode: Order[], eircode: string, groupKey: string) => {
    // Get all unique group keys for sequential color assignment
    const allGroupKeys = Array.from(validEircodeGroups.keys());
    const orderColor = getColorForEircodeGroup(ordersByEircode, allGroupKeys, groupKey);

    // Extract order IDs for linking
    const orderIds = ordersByEircode.map(order => order.orderShortId);

    ordersByEircode.forEach(order => {
      const existingColorPairing = orderIdsWithMarkedPairings[order.orderShortId];

      if (existingColorPairing) {
        orderIdsWithMarkedPairings[order.orderShortId] = {
          ...existingColorPairing,
          linkedOverweightOrderIds: orderIds.filter(id => id !== order.orderShortId) // Exclude self from linkedOverweightOrderIds
        };
      } else {
        orderIdsWithMarkedPairings[order.orderShortId] = {
          color: orderColor,
          eircode: eircode,
          linkedOverweightOrderIds: orderIds.filter(id => id !== order.orderShortId) // Exclude self from linkedOverweightOrderIds
        };
      }
    });
  };

  // Process orders and mark those with eircode pairings
  validEircodeGroups.forEach((ordersByEircode, groupKey) => {
      if (ordersByEircode.length === 1) return;

      if (ordersByEircode.length > 1) {
          if (checkForExraDeliveryStores(ordersByEircode)) {
            // Get the actual eircode from the first order (all orders in group have same eircode)
            const eircode = ordersByEircode[0].eircode;
            assignOverweightOrderPairing(ordersByEircode, eircode, groupKey);
          }
      }
  });

  return orderIdsWithMarkedPairings;
};

export const sortOrdersByPriority = (orderA, orderB) => {
  const timeA = orderA.storePreparedDate ? new Date(orderA.storePreparedDate).getTime() : 0;
  const timeB = orderB.storePreparedDate ? new Date(orderB.storePreparedDate).getTime() : 0;
  return timeA - timeB;
};

/**
 * Converts a status string from UPPER_CASE_WITH_UNDERSCORES format to Title Case Without Underscores
 * Example: "STORE_ACCEPTED" becomes "StoreAccepted"
 * 
 * @param {string} status - The order status in UPPER_CASE_WITH_UNDERSCORES format
 * @returns {string} The formatted status in Title Case Without Underscores
 */
export const formatOrderStatus = (status) => {
  if (!status) return '';
  
  return status
    .split('_')
    .map(word => {
      return word.toLowerCase().charAt(0).toUpperCase() + word.toLowerCase().slice(1);
    })
    .join(' '); 
};

export const NOT_IN_PROGRESS_STATUSES = ['RESET', 'CANCELLED', 'COMPLETE', 'DELIVERED', 'REJECTED', 'STORE_REJECTED'];

/**
 * Represents a Landing Pad group with its orders
 */
export interface PadGroup {
  id: number;
  name: string;
  aircraft: string;
  orders: Order[];
}

/**
 * Represents a landing zone group with its Landing Pads
 */
export interface LandingZoneGroup {
  id: string;
  name: string;
  pads: PadGroup[];
}

/**
 * Groups orders by landing zone and then by pad 
 * @param orders - Array of orders to group
 * @param pads - Array of pads to use for the landing zone groups
 * @param filterByStatuses - Optional array of order statuses to filter by
 * @returns An array of landing zone groups, each containing pad groups with their orders
 */
export const groupOrdersByLandingZoneAndPad = (
  orders: Order[],
  pads?: LandingPad[],
  filterByStatuses?: string[]
): LandingZoneGroup[] => {
  // Create a map for O(1) pad lookups
  const padMap = new Map(pads?.map(pad => [pad.id, pad]) || []);
  
  // Filter orders once
  const filteredOrders = filterByStatuses 
    ? orders.filter(order => filterByStatuses.includes(order.status))
    : orders;

  // Use nested maps for efficient lookups
  const landingZonesMap = new Map<string, {
    data: LandingZoneGroup,
    padsMap: Map<number | null, PadGroup>
  }>();

  filteredOrders.forEach(order => {
    const landingZoneId = order.landingZoneId ?? 'unassigned';
    const landingZoneName = order.landingZoneName ?? 'No Landing Zone';
    const padId = order.originLandingPadId ?? null;
    
    // Get or create landing zone
    if (!landingZonesMap.has(landingZoneId)) {
      landingZonesMap.set(landingZoneId, {
        data: {
          id: landingZoneId,
          name: landingZoneName,
          pads: []
        },
        padsMap: new Map()
      });
    }
    
    const landingZone = landingZonesMap.get(landingZoneId)!;
    
    // Get or create pad group
    if (!landingZone.padsMap.has(padId)) {
      const pad = padId ? padMap.get(padId) : null;
      const padGroup: PadGroup = {
        id: padId,
        name: pad?.name || 'No Pad',
        aircraft: pad?.aircraft ? `MNA-${pad.aircraft}` : 'Assign Aircraft',
        orders: []
      };
      landingZone.padsMap.set(padId, padGroup);
      landingZone.data.pads.push(padGroup);
    }
    
    // Add order to pad group
    landingZone.padsMap.get(padId)!.orders.push(order);
  });

  // Return just the landing zone data
  return Array.from(landingZonesMap.values()).map(lz => lz.data);
};

/**
 * Sorts orders based on NextUp tags and expected delivery date
 * @param orders - Array of orders to sort
 * @returns Sorted array of orders
 */
export const sortOrdersByPriorityAndDate = (orders: Order[]): Order[] => {
  return [...orders].sort((a, b) => {
    // Priority to orders tagged with 'NEXT'
    if (a.tags?.includes('NEXT') && !b.tags?.includes('NEXT')) {
      return -1;
    } else if (!a.tags?.includes('NEXT') && b.tags?.includes('NEXT')) {
      return 1;
    }
    // Otherwise sort by expected delivery date
    return (
      new Date(a.expectedDeliveryDate).getTime() -
      new Date(b.expectedDeliveryDate).getTime()
    );
  });
};

export const canChangeLandingZone = (missionStatus: string) => {
  return !['HOLD', 'COMPLETED', 'IN_PROGRESS', 'MANUAL'].includes(missionStatus);
}